/**
 * ===================================================
 * SETUP MODULE - Applies Configuration to the Experience
 * ===================================================
 * 
 * This module reads the config.js file and automatically sets up:
 * - CSS styling based on theme colors
 * - HTML content for panels and menus
 * - Document title
 */

import { CONFIG } from '../config.js';

/**
 * Apply theme colors to CSS custom properties
 */
export function applyTheme(sceneConfig = CONFIG) {
    const root = document.documentElement;
    const theme = sceneConfig.theme || CONFIG.theme;

    // Background gradient
    if (theme.backgroundGradient) {
        const bg = document.querySelector('.background-gradient');
        if (bg) {
            bg.style.background = `linear-gradient(to bottom, ${theme.backgroundGradient.top}, ${theme.backgroundGradient.bottom})`;
        }
    }

    // Navigation bar
    const topNav = document.querySelector('.top-nav');
    if (topNav && theme.navBar) {
        topNav.style.backgroundColor = theme.navBar.backgroundColor;
        topNav.style.height = theme.navBar.height;

        if (theme.navBar.hamburgerColor) {
            const hamburger = document.getElementById('hamburger-btn');
            if (hamburger) hamburger.style.color = theme.navBar.hamburgerColor;
        } else {
            // Reset to default
            const hamburger = document.getElementById('hamburger-btn');
            if (hamburger) hamburger.style.color = '#333';
        }
    }

    // Logo (Handled by script.js mostly, but applying style here)
    const logo = document.querySelector('.logo');
    if (logo && theme.navBar) {
        // logo.src check is done in script.js for per-scene logo
        logo.style.height = theme.navBar.logoHeight;
    }

    // Side panel
    const infoPanel = document.querySelector('.info-panel');
    if (infoPanel && theme.sidePanel) {
        infoPanel.style.backgroundColor = theme.sidePanel.backgroundColor;
        infoPanel.style.color = theme.sidePanel.textColor;
        infoPanel.style.width = theme.sidePanel.width;

        // Panel Placement (Left vs Right)
        if (theme.sidePanel.placement === 'right') {
            infoPanel.classList.add('right-sidebar');
            // Remove inline styles that might conflict or persist
            infoPanel.style.left = '';
            infoPanel.style.right = '';
            infoPanel.style.borderTopLeftRadius = '';
            infoPanel.style.borderTopRightRadius = '';
        } else {
            // Default Left (Bottom Sheet style)
            infoPanel.classList.remove('right-sidebar');
            infoPanel.style.left = '0';
            infoPanel.style.right = 'auto';
            infoPanel.style.borderTopLeftRadius = '20px';
            infoPanel.style.borderTopRightRadius = '20px';
        }
    }

    // Left Sidebar (Navigation Panel) - Match Side Panel Theme
    const leftSidebar = document.querySelector('.left-sidebar');
    if (leftSidebar && theme.sidePanel) {
        leftSidebar.style.backgroundColor = theme.sidePanel.backgroundColor;
        leftSidebar.style.color = theme.sidePanel.textColor;

        // Update Links color
        const links = leftSidebar.querySelectorAll('a');
        links.forEach(link => {
            link.style.color = theme.sidePanel.textColor;
            // Add hover effect listener if needed, but CSS :hover might be tricky with inline.
            // Simplified: Inline override might break CSS hover background unless we use !important or CSS vars.
            // For now, text color is enough.
        });

        // Update Close Button
        const closeBtn = leftSidebar.querySelector('.close-button');
        if (closeBtn) {
            closeBtn.style.color = theme.sidePanel.textColor;
        }

        // Update Title (H3)
        const title = leftSidebar.querySelector('h3');
        if (title) {
            title.style.color = theme.sidePanel.textColor;
        }
    }

    // Primary buttons
    if (theme.sidePanel) {
        let styleTag = document.getElementById('theme-style-override');
        if (!styleTag) {
            styleTag = document.createElement('style');
            styleTag.id = 'theme-style-override';
            document.head.appendChild(styleTag);
        }
        styleTag.textContent = `
            .action-button.primary {
                background-color: ${theme.sidePanel.primaryButtonColor} !important;
                color: ${theme.sidePanel.buttonTextColor} !important;
            }
        `;
    }



    // Instruction Panel
    const instructionPanel = document.getElementById('instruction-panel');
    if (instructionPanel) {
        // Default Blue
        let newBg = 'rgba(0, 31, 63, 0.8)';

        if (theme.instructionPanel && theme.instructionPanel.backgroundColor) {
            newBg = theme.instructionPanel.backgroundColor;
        }

        instructionPanel.style.background = newBg;

        if (theme.instructionPanel) {
            if (theme.instructionPanel.exploreText) {
                const exploreEl = document.getElementById('explore-instruction');
                if (exploreEl) exploreEl.innerHTML = theme.instructionPanel.exploreText;
            }

            if (theme.instructionPanel.topBannerText) {
                const topBanner = document.getElementById('top-instruction-label');
                if (topBanner) topBanner.textContent = theme.instructionPanel.topBannerText;
            }
        }

        // Apply Reset Button Color
        const resetButton = document.getElementById('back-button');
        const defaultResetColor = theme.resetButtonColor || CONFIG.theme.resetButtonColor || '#df5e00';
        if (resetButton) resetButton.style.background = defaultResetColor;
    }

    // Vignette Overlay
    const vignetteOverlay = document.querySelector('.vignette-overlay');
    if (vignetteOverlay) {
        if (theme.vignette) {
            vignetteOverlay.classList.add('active');
            // Allow customization
            if (typeof theme.vignette === 'string') {
                vignetteOverlay.style.background = theme.vignette;
            } else if (theme.vignette.background) {
                vignetteOverlay.style.background = theme.vignette.background;
            }
        } else {
            vignetteOverlay.classList.remove('active');
        }
    }

    // Hide/Show Panel Image support
    if (theme.hidePanelImage) {
        document.body.classList.add('hide-panel-image');
    } else {
        document.body.classList.remove('hide-panel-image');
    }
}

/**
 * Set page title
 */
export function setPageTitle() {
    document.title = CONFIG.settings.pageTitle;
}

/**
 * Setup menu links
 */
export function setupMenuLinks() {
    const sidebarLinks = document.querySelector('.sidebar-links');
    if (!sidebarLinks) return;

    // Clear existing links
    sidebarLinks.innerHTML = '';

    // Add Home Link
    const homeLi = document.createElement('li');
    const homeA = document.createElement('a');
    homeA.href = "#";
    homeA.textContent = "🧊 See Other Examples";
    homeA.onclick = (e) => {
        e.preventDefault();
        if (window.returnToHome) window.returnToHome();
        // Close sidebar
        const sidebar = document.getElementById('left-sidebar');
        if (sidebar) sidebar.classList.remove('active');
    };
    homeLi.appendChild(homeA);
    sidebarLinks.appendChild(homeLi);

    // Add configured links
    CONFIG.menuLinks.forEach(link => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = link.url;
        a.textContent = link.label;
        li.appendChild(a);
        sidebarLinks.appendChild(li);
    });


}

/**
 * Update panel content dynamically
 */
export function updatePanelContent(panelName, customData = null) {
    let titleText, descriptionText, imageSrc;
    let buttons = [];

    // 1. Check Custom Data (Direct Injection)
    if (customData) {
        titleText = customData.title;
        descriptionText = customData.description;
        imageSrc = customData.image;
    }
    // 2. Fallback to Config Data
    else {
        const panelConfig = CONFIG.panels[panelName];
        if (!panelConfig) {
            console.warn(`No panel configuration found for: ${panelName}`);
            return;
        }
        titleText = panelConfig.title;
        descriptionText = panelConfig.description;
        titleText = panelConfig.title;
        buttons = panelConfig.buttons || [];
        imageSrc = panelConfig.image;
    }


    const panel = document.getElementById('info-panel');
    const title = panel.querySelector('.panel-header h2');
    const content = panel.querySelector('.panel-content');

    // Update title
    title.textContent = titleText;

    // Build content HTML
    let buttonsHTML = '';
    buttons.forEach((btn, index) => {
        buttonsHTML += `
            <button class="action-button ${btn.type}" data-panel-button="${index}">
                ${btn.label}
            </button>
        `;
    });

    // Default Image if none provided (optional, or just logic to show placeholder)
    if (!imageSrc) {
        // Example placeholder or keep empty. 
        // For now, let's allow a placeholder slot if specifically requested, 
        // but user asked for "a spot for image".
        // Let's assume we want to show a placeholder if no specific image is set, 
        // or just ensure the container exists for future use.
        // Better: Use a placeholder image path.
        imageSrc = "https://placehold.co/600x400/1a2b3c/ffffff?text=Part+Image";
    }

    // Always add a "Learn More" button if it's not custom-defined
    let hasLearnMore = buttons.some(b => b.label === "Learn More");
    if (!hasLearnMore) {
        buttonsHTML += `
            <button class="action-button primary" id="default-learn-more">
                Learn More
            </button>
        `;
    }

    content.innerHTML = `
        <div class="panel-image-container">
             <img src="${imageSrc}" alt="Part Image" class="panel-image" onerror="this.style.display='none'">
        </div>
        <p>${descriptionText}</p>
        ${buttonsHTML}
    `;

    // Attach button event listeners
    buttons.forEach((btn, index) => {
        const buttonEl = content.querySelector(`[data-panel-button="${index}"]`);
        if (buttonEl && btn.action) {
            buttonEl.addEventListener('click', btn.action);
        }
    });

    // Attach listener for default learn more
    const defaultLearnMoreBtn = content.querySelector('#default-learn-more');
    if (defaultLearnMoreBtn) {
        defaultLearnMoreBtn.addEventListener('click', () => {
            const modal = document.getElementById('learn-more-modal');
            if (modal) {
                // Optional: Update modal content based on the panel content
                const modalTitle = modal.querySelector('#modal-title');
                const modalBody = modal.querySelector('#modal-body');

                if (modalTitle) modalTitle.textContent = titleText + " - Details";
                if (modalBody) {
                    modalBody.innerHTML = `
                        <p><strong>Detailed Specifications for ${titleText}:</strong></p>
                        <p>${descriptionText}</p>
                        <p>Additional technical data, diagrams, or extended descriptions would typically be loaded here from a CMS or configuration file.</p>
                        <hr>
                        <ul>
                            <li><strong>Material:</strong> Biological Tissue / Grey Matter</li>
                            <li><strong>Function:</strong> ${descriptionText}</li>
                            <li><strong>Connectivity:</strong> Highly connected neural network</li>
                        </ul>
                    `;
                }

                modal.classList.add('active');
            }
        });
    }

    // Ensure panel is open? (Usually handled by callee, but nice to have)
    // panel.classList.add('active'); 


}

/**
 * Toggle back button visibility
 */
export function setupBackButton() {
    const backButton = document.getElementById('back-button');
    if (!backButton) return;

    if (!CONFIG.settings.showBackButton) {
        backButton.style.display = 'none';
    } else {
        backButton.addEventListener('click', () => {
            if (window.SceneManager && window.SceneManager.resetView) {
                window.SceneManager.resetView();
            }
        });
    }
}

/**
 * Setup hamburger menu
 */
function setupHamburgerMenu() {
    const hamburgerBtn = document.getElementById('hamburger-btn');
    const closeBtn = document.getElementById('close-left-sidebar');
    const sidebar = document.getElementById('left-sidebar');

    if (hamburgerBtn && sidebar) {
        hamburgerBtn.addEventListener('click', () => {
            sidebar.classList.toggle('active');
        });
    }

    if (closeBtn && sidebar) {
        closeBtn.addEventListener('click', () => {
            sidebar.classList.remove('active');
        });
    }
}

/**
 * Setup panel close button
 */
function setupPanelCloseButton() {
    const closeBtn = document.getElementById('close-panel');
    const panel = document.getElementById('info-panel');
    if (closeBtn && panel) {
        closeBtn.addEventListener('click', () => {
            panel.classList.remove('active');
            if (window.SceneManager && window.SceneManager.onPanelClosed) {
                window.SceneManager.onPanelClosed();
            }
        });
    }
}

/**
 * Initialize all setup functions
 */
export function initializeSetup() {


    applyTheme();
    setPageTitle();
    setupMenuLinks();
    setupBackButton();
    setupHamburgerMenu();
    setupPanelCloseButton();


}
